import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  userRole: string | null;
  userRoles: string[];
  hasActiveSubscription: boolean;
  loadingSubscription: boolean;
  subscriptionType: string | null;
  subscriptionEndDate: string | null;
  isLifetimeSubscription: boolean;
  hasCompletedScreening: boolean;
  screeningStatus: string | null;
  hasBillingData: boolean;
  loadingBillingData: boolean;
  refreshBillingData: () => Promise<void>;
  refreshScreeningStatus: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string, affiliateCode?: string, language?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string, returnUrl?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  isSubscriber: () => boolean;
  isAdmin: () => boolean;
  isPro: () => boolean;
  isPlus: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<string | null>(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<string | null>(null);
  const [isLifetimeSubscription, setIsLifetimeSubscription] = useState(false);
  const [hasCompletedScreening, setHasCompletedScreening] = useState(false);
  const [screeningStatus, setScreeningStatus] = useState<string | null>(null);
  const [hasBillingData, setHasBillingData] = useState(false);
  const [loadingBillingData, setLoadingBillingData] = useState(false);
  const navigate = useNavigate();

  // Helper function to determine primary role
  const getPrimaryRole = (roles: string[]): string | null => {
    if (roles.includes('admin')) return 'admin';
    if (roles.includes('moderator')) return 'moderator';
    if (roles.includes('registered')) return 'registered';
    return null;
  };

  // Fetch user roles
  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) throw error;
      
      // Extract all roles into an array
      const roles = data?.map(r => r.role) || [];
      setUserRoles(roles);
      setUserRole(getPrimaryRole(roles));
    } catch (error) {
      console.error("Error fetching user roles:", error);
      setUserRoles([]);
      setUserRole(null);
    }
  };

  // Check screening status
  const checkScreeningStatus = async (userId: string, roles: string[]) => {
    try {
      // Bypass for admin: automatically verified
      if (roles.includes('admin')) {
        setHasCompletedScreening(true);
        setScreeningStatus('approved');
        return;
      }

      const { data, error } = await supabase
        .from('screening_applications')
        .select('status')
        .eq('user_id', userId)
        .order('submitted_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking screening:', error);
        return;
      }

      if (data) {
        setHasCompletedScreening(true);
        setScreeningStatus(data.status);
      } else {
        setHasCompletedScreening(false);
        setScreeningStatus(null);
      }
    } catch (error) {
      console.error('Error checking screening:', error);
    }
  };

  // Check active subscription
  const checkActiveSubscription = async (userId: string) => {
    setLoadingSubscription(true);
    try {
      // Use limit(1) to avoid PGRST116 error when multiple active rows exist
      const { data: rows, error } = await supabase
        .from("subscriptions")
        .select("*, product_type, stripe_subscription_id, current_period_end")
        .eq("user_id", userId)
        .eq("status", "active")
        .in("product_type", ["base", "pro", "plus"])
        .gte("current_period_end", new Date().toISOString())
        .order("created_at", { ascending: false })
        .limit(1);
      
      const data = rows && rows.length > 0 ? rows[0] : null;

      if (error && error.code !== 'PGRST116') {
        console.error("Error checking subscription:", error);
        setHasActiveSubscription(false);
        setSubscriptionType(null);
        setSubscriptionEndDate(null);
        setIsLifetimeSubscription(false);
        return;
      }

      setHasActiveSubscription(!!data);
      setSubscriptionType(data?.product_type || null);
      setSubscriptionEndDate(data?.current_period_end || null);
      // Check if this is a lifetime subscription (admin_lifetime_)
      setIsLifetimeSubscription(data?.stripe_subscription_id?.startsWith('admin_lifetime_') || false);
    } catch (error) {
      console.error("Error checking subscription:", error);
      setHasActiveSubscription(false);
      setSubscriptionType(null);
      setSubscriptionEndDate(null);
      setIsLifetimeSubscription(false);
    } finally {
      setLoadingSubscription(false);
    }
  };

  // Check billing data
  const checkBillingData = async (userId: string) => {
    setLoadingBillingData(true);
    try {
      const { data, error } = await supabase
        .from("billing_data")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error("Error checking billing data:", error);
        setHasBillingData(false);
        return;
      }

      setHasBillingData(!!data);
    } catch (error) {
      console.error("Error checking billing data:", error);
      setHasBillingData(false);
    } finally {
      setLoadingBillingData(false);
    }
  };

  const refreshBillingData = async () => {
    if (user?.id) {
      await checkBillingData(user.id);
    }
  };

  const refreshScreeningStatus = async () => {
    if (user?.id) {
      await checkScreeningStatus(user.id, userRoles);
    }
  };

  const refreshSubscription = async () => {
    if (user?.id) {
      await checkActiveSubscription(user.id);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer Supabase calls with setTimeout to avoid deadlock
        if (session?.user) {
          setTimeout(async () => {
            try {
              const { data, error } = await supabase
                .from("user_roles")
                .select("role")
                .eq("user_id", session.user.id);

              if (error) throw error;
              
              const roles = data?.map(r => r.role) || [];
              setUserRoles(roles);
              setUserRole(getPrimaryRole(roles));
              
              // Check screening status with roles
              await checkScreeningStatus(session.user.id, roles);
            } catch (error) {
              console.error("Error fetching user roles:", error);
              setUserRoles([]);
              setUserRole(null);
            }
          }, 0);
        } else {
          setUserRole(null);
          setUserRoles([]);
          setHasCompletedScreening(false);
          setScreeningStatus(null);
        }
        
        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(async () => {
          try {
            const { data, error } = await supabase
              .from("user_roles")
              .select("role")
              .eq("user_id", session.user.id);

            if (error) throw error;
            
            const roles = data?.map(r => r.role) || [];
            setUserRoles(roles);
            setUserRole(getPrimaryRole(roles));
            
            // Check screening status with roles
            await checkScreeningStatus(session.user.id, roles);
          } catch (error) {
            console.error("Error fetching user roles:", error);
            setUserRoles([]);
            setUserRole(null);
          }
        }, 0);
      }
      
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check subscription and billing data when user changes
  useEffect(() => {
    if (user?.id) {
      checkActiveSubscription(user.id);
      checkBillingData(user.id);
    } else {
      setHasActiveSubscription(false);
      setSubscriptionEndDate(null);
      setIsLifetimeSubscription(false);
      setHasBillingData(false);
    }
  }, [user?.id]);

  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    affiliateCode?: string,
    language?: string
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const userData: Record<string, string> = {
      first_name: firstName,
      last_name: lastName,
      preferred_language: language || 'it',
    };
    
    // Add affiliate code to user metadata if provided
    if (affiliateCode) {
      userData.affiliate_code = affiliateCode;
    }
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: userData,
      },
    });

    // Invia email di benvenuto se la registrazione è andata a buon fine
    if (!error) {
      try {
        await supabase.functions.invoke('send-welcome-email', {
          body: {
            type: 'INSERT',
            record: {
              email,
              raw_user_meta_data: {
                first_name: firstName,
                last_name: lastName,
              },
            },
          },
        });
        if (import.meta.env.DEV) console.log('Welcome email queued successfully');
      } catch (emailError) {
        // Non blocchiamo la registrazione se l'email fallisce
        console.error('Failed to queue welcome email:', emailError);
      }
    }

    return { error };
  };

  const signIn = async (email: string, password: string, returnUrl?: string) => {
    // Route through edge function for server-side rate limiting
    const { data: response, error: invokeError } = await supabase.functions.invoke('auth-login', {
      body: { email, password },
    });

    if (invokeError) {
      // Network or invocation error
      return { error: { message: invokeError.message || 'Login failed' } };
    }

    // Check for application-level errors from the edge function
    if (response?.error) {
      return { error: { message: response.error === 'rate_limited' || response.error === 'Too many requests' ? 'rate_limited' : response.error === 'invalid_credentials' ? response.message : response.message || 'Login failed' } };
    }

    // Set the session from the edge function response
    if (response?.session) {
      await supabase.auth.setSession({
        access_token: response.session.access_token,
        refresh_token: response.session.refresh_token,
      });
      navigate(returnUrl || "/dashboard");
      return { error: null };
    }

    return { error: { message: 'Unexpected login error' } };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUserRole(null);
    setUserRoles([]);
    setHasActiveSubscription(false);
    setSubscriptionType(null);
    setSubscriptionEndDate(null);
    setIsLifetimeSubscription(false);
    setHasCompletedScreening(false);
    setScreeningStatus(null);
    setHasBillingData(false);
    
    // Clear coupon sessionStorage
    sessionStorage.removeItem('pendingCoupon');
    sessionStorage.removeItem('appliedCouponData');
    
    navigate("/");
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth/reset-password`;
    
    const { data: response, error: invokeError } = await supabase.functions.invoke('auth-reset-password', {
      body: { email, redirectTo: redirectUrl },
    });

    if (invokeError) {
      return { error: { message: invokeError.message || 'Reset failed' } };
    }

    if (response?.error && response.error !== 'server_error') {
      return { error: { message: response.error === 'Too many requests' ? 'rate_limited' : response.message || 'Reset failed' } };
    }

    return { error: null };
  };

  const isSubscriber = () => {
    return userRoles.includes("admin") || hasActiveSubscription;
  };

  const isAdmin = () => {
    return userRoles.includes("admin");
  };

  const isPro = () => {
    return userRoles.includes("admin") || subscriptionType === 'pro' || subscriptionType === 'plus';
  };

  const isPlus = () => {
    return userRoles.includes("admin") || subscriptionType === 'plus';
  };

  const value = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    userRole,
    userRoles,
    hasActiveSubscription,
    loadingSubscription,
    subscriptionType,
    subscriptionEndDate,
    isLifetimeSubscription,
    hasCompletedScreening,
    screeningStatus,
    hasBillingData,
    loadingBillingData,
    refreshBillingData,
    refreshScreeningStatus,
    refreshSubscription,
    signUp,
    signIn,
    signOut,
    resetPassword,
    isSubscriber,
    isAdmin,
    isPro,
    isPlus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
